// ===========================================
import { useTranslation } from 'react-i18next';
// 💳 COMPONENTE DE PRODUCTOS FINANCIEROS: src/components/AGTFin/dashboard/products/FinProducts.js
// ===========================================

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  Home as HomeIcon,
  ShowChart as ShowChartIcon,
  Percent as PercentIcon,
  WarningAmber as WarningAmberIcon,
  Link as LinkIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import agtFinService from '../../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES MODERNA
// ===========================================
const getGrayscalePalette = (theme) => ({
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  gradients: {
    primary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    card: theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,1) 100%)'
  },
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
    card: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'
  }
});

// ===========================================
// 🎯 COMPONENTES ESTILIZADOS MODERNOS
// ===========================================
const ModernContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  background: theme.palette.mode === 'dark'
    ? 'rgba(0,0,0,0.2)'
    : 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.1)'
    : '1px solid rgba(0,0,0,0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 16,
  }
}));

const SummaryCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255,255,255,0.1)'
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.primary,
    }
  };
});

const ProductCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 18,
    background: colors.backgrounds.card,
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255,255,255,0.08)'
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 24px rgba(0,0,0,0.25)'
      : '0 6px 20px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 10px 30px rgba(0,0,0,0.35)'
        : '0 10px 30px rgba(0,0,0,0.12)'
    }
  };
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
}));

const getProductTypes = (t) => [
  { value: 'credit_card', label: t('agtfin.products.productTypes.credit_card'), icon: <CreditCardIcon /> },
  { value: 'credit_line', label: t('agtfin.products.productTypes.credit_line'), icon: <AccountBalanceIcon /> },
  { value: 'consumer_loan', label: t('agtfin.products.productTypes.consumer_loan'), icon: <TrendingDownIcon /> },
  { value: 'mortgage', label: t('agtfin.products.productTypes.mortgage'), icon: <HomeIcon /> }
];

const getStatusOptions = (t) => [
  { value: 'active', label: t('agtfin.products.status.active') },
  { value: 'paused', label: t('agtfin.products.status.paused') },
  { value: 'closed', label: t('agtfin.products.status.closed') }
];

const initialFormState = {
  productType: 'credit_card',
  name: '',
  institution: '',
  interestRate: '',
  status: 'active',
  creditLimit: '',
  balanceUsed: '',
  balanceAvailable: '',
  originalAmount: '',
  currentBalance: '',
  monthlyPayment: '',
  termMonths: '',
  paymentsMade: '',
  paymentsTotal: '',
  billingDate: '',
  paymentDueDate: '',
  reminderDays: '7',
  collateral: '',
  notes: '',
  linkedBillId: '',
  linkedExpenseId: '',
  currency: ''
};

const parseOptionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatDateInput = (value) => value || '';

const daysBetween = (fromDate, toDate) => {
  const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  const diffMs = end.getTime() - start.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const FinProducts = ({ user, userProfile }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = useMemo(() => getGrayscalePalette(theme), [theme]);

  const [products, setProducts] = useState([]);
  const [bills, setBills] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [formError, setFormError] = useState('');
  const [calculator, setCalculator] = useState({
    balance: '',
    rate: '',
    months: '12'
  });

  const currency = userProfile?.currency || 'USD';
  const formatCurrency = useCallback((amount) => {
    return agtFinService.formatCurrency(amount || 0, currency);
  }, [currency]);

  const billsLookup = useMemo(() => {
    const map = {};
    bills.forEach((bill) => {
      map[bill.billId] = bill.name;
    });
    return map;
  }, [bills]);

  const expensesLookup = useMemo(() => {
    const map = {};
    expenses.forEach((expense) => {
      map[expense.expenseId] = expense.name;
    });
    return map;
  }, [expenses]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [productsResponse, billsResponse, expensesResponse] = await Promise.all([
        agtFinService.getFinancialProducts(),
        agtFinService.apiClient.get('/agtfin/bills'),
        agtFinService.apiClient.get('/agtfin/expenses')
      ]);

      setProducts(productsResponse.products || []);
      setBills(billsResponse.data?.bills || []);
      setExpenses(expensesResponse.data?.expenses || []);
    } catch (err) {
      console.error('Error cargando productos financieros:', err);
      setError(t('agtfin.products.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalDebt = useMemo(() => {
    return products.reduce((sum, product) => {
      const balance = product.currentBalance ?? product.balanceUsed ?? 0;
      return sum + Number(balance || 0);
    }, 0);
  }, [products]);

  const totalAvailableCredit = useMemo(() => {
    return products.reduce((sum, product) => {
      if (!['credit_card', 'credit_line'].includes(product.productType)) {
        return sum;
      }
      const limit = Number(product.creditLimit || 0);
      const used = Number(product.balanceUsed || 0);
      const available = Math.max(limit - used, 0);
      return sum + available;
    }, 0);
  }, [products]);

  const averageRate = useMemo(() => {
    if (products.length === 0) {
      return 0;
    }
    const weighted = products.reduce((acc, product) => {
      const weight = Number(product.currentBalance ?? product.balanceUsed ?? 0);
      return {
        totalRate: acc.totalRate + (product.interestRate || 0) * weight,
        totalWeight: acc.totalWeight + weight
      };
    }, { totalRate: 0, totalWeight: 0 });

    if (weighted.totalWeight > 0) {
      return weighted.totalRate / weighted.totalWeight;
    }

    const total = products.reduce((sum, product) => sum + (product.interestRate || 0), 0);
    return total / products.length;
  }, [products]);

  const lowestRateId = useMemo(() => {
    if (products.length === 0) return null;
    return products.reduce((lowest, product) => {
      if (!lowest || (product.interestRate || 0) < (lowest.interestRate || 0)) {
        return product;
      }
      return lowest;
    }, null)?.productId;
  }, [products]);

  const handleOpenDialog = (product = null) => {
    setFormError('');
    if (product) {
      setEditingProduct(product);
      setFormData({
        productType: product.productType || 'credit_card',
        name: product.name || '',
        institution: product.institution || '',
        interestRate: product.interestRate ?? '',
        status: product.status || 'active',
        creditLimit: product.creditLimit ?? '',
        balanceUsed: product.balanceUsed ?? '',
        balanceAvailable: product.balanceAvailable ?? '',
        originalAmount: product.originalAmount ?? '',
        currentBalance: product.currentBalance ?? '',
        monthlyPayment: product.monthlyPayment ?? '',
        termMonths: product.termMonths ?? '',
        paymentsMade: product.paymentsMade ?? '',
        paymentsTotal: product.paymentsTotal ?? '',
        billingDate: formatDateInput(product.billingDate),
        paymentDueDate: formatDateInput(product.paymentDueDate),
        reminderDays: product.reminderDays ?? '7',
        collateral: product.collateral || '',
        notes: product.notes || '',
        linkedBillId: product.linkedBillId || '',
        linkedExpenseId: product.linkedExpenseId || '',
        currency: product.currency || ''
      });
    } else {
      setEditingProduct(null);
      setFormData(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFormError('');
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError(t('agtfin.products.messages.nameRequired'));
      return;
    }
    if (formData.interestRate === '' || formData.interestRate === null) {
      setFormError(t('agtfin.products.messages.rateRequired'));
      return;
    }

    const creditLimit = parseOptionalNumber(formData.creditLimit);
    const balanceUsed = parseOptionalNumber(formData.balanceUsed);
    const computedAvailable = (creditLimit !== null && balanceUsed !== null)
      ? Math.max(creditLimit - balanceUsed, 0)
      : parseOptionalNumber(formData.balanceAvailable);

    const payload = {
      productType: formData.productType,
      name: formData.name.trim(),
      institution: formData.institution.trim(),
      interestRate: parseOptionalNumber(formData.interestRate),
      status: formData.status,
      creditLimit,
      balanceUsed,
      balanceAvailable: computedAvailable,
      originalAmount: parseOptionalNumber(formData.originalAmount),
      currentBalance: parseOptionalNumber(formData.currentBalance),
      monthlyPayment: parseOptionalNumber(formData.monthlyPayment),
      termMonths: parseOptionalNumber(formData.termMonths),
      paymentsMade: parseOptionalNumber(formData.paymentsMade),
      paymentsTotal: parseOptionalNumber(formData.paymentsTotal),
      billingDate: formData.billingDate || null,
      paymentDueDate: formData.paymentDueDate || null,
      reminderDays: parseOptionalNumber(formData.reminderDays),
      collateral: formData.collateral.trim(),
      notes: formData.notes.trim(),
      linkedBillId: formData.linkedBillId || null,
      linkedExpenseId: formData.linkedExpenseId || null,
      currency: formData.currency || currency
    };

    try {
      if (editingProduct) {
        await agtFinService.updateFinancialProduct(editingProduct.productId, payload);
      } else {
        await agtFinService.createFinancialProduct(payload);
      }
      handleCloseDialog();
      await loadData();
    } catch (err) {
      console.error('Error guardando producto financiero:', err);
      setFormError(t('agtfin.products.messages.saveError'));
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(t('agtfin.products.messages.confirmDelete'))) {
      return;
    }
    try {
      await agtFinService.deleteFinancialProduct(productId);
      await loadData();
    } catch (err) {
      console.error('Error eliminando producto financiero:', err);
      setError(t('agtfin.products.messages.deleteError'));
    }
  };

  const getProductTypeLabel = (type) => {
    return getProductTypes(t).find((item) => item.value === type)?.label || t('agtfin.products.productTypes.product');
  };

  const getStatusLabel = (status) => {
    return getStatusOptions(t).find((item) => item.value === status)?.label || status;
  };

  const getPaymentAlert = (product) => {
    if (!product.paymentDueDate) {
      return null;
    }
    const dueDate = new Date(product.paymentDueDate);
    if (Number.isNaN(dueDate.getTime())) {
      return null;
    }
    const daysLeft = daysBetween(new Date(), dueDate);
    const reminderDays = Number(product.reminderDays ?? 7);
    if (daysLeft < 0) {
      return { label: t('agtfin.products.card.overduePayment'), color: 'error' };
    }
    if (daysLeft <= reminderDays) {
      return { label: `Pago en ${daysLeft} días`, color: 'warning' };
    }
    return null;
  };

  const calculateInterest = () => {
    const balance = parseOptionalNumber(calculator.balance) || 0;
    const rate = parseOptionalNumber(calculator.rate) || 0;
    const months = parseOptionalNumber(calculator.months) || 12;
    const monthlyRate = rate / 100 / 12;
    const totalInterest = balance * monthlyRate * months;
    return totalInterest;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <ModernContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <SectionTitle variant={isMobile ? 'h6' : 'h5'}>
            {t('agtfin.products.title')}
          </SectionTitle>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              background: colors.gradients.primary,
              color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'
            }}
          >
            {t('agtfin.products.new')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <SummaryCard>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {t('agtfin.products.summary.totalDebt')}
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(totalDebt)}
                </Typography>
              </CardContent>
            </SummaryCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {t('agtfin.products.summary.availableCredit')}
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(totalAvailableCredit)}
                </Typography>
              </CardContent>
            </SummaryCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Tasa promedio
                </Typography>
                <Typography variant="h6">
                  {averageRate.toFixed(2)}%
                </Typography>
              </CardContent>
            </SummaryCard>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {products.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6, color: colors.tertiary }}>
                <ShowChartIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">
                  Aún no tienes productos financieros registrados.
                </Typography>
              </Box>
            </Grid>
          ) : (
            products.map((product) => {
              const isCredit = ['credit_card', 'credit_line'].includes(product.productType);
              const limit = Number(product.creditLimit || product.originalAmount || 0);
              const used = Number(product.balanceUsed || product.currentBalance || 0);
              const utilization = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
              const paymentAlert = getPaymentAlert(product);

              const paymentsMade = Number(product.paymentsMade || 0);
              const paymentsTotal = Number(product.paymentsTotal || 0);
              const paymentProgress = paymentsTotal > 0
                ? Math.min((paymentsMade / paymentsTotal) * 100, 100)
                : (limit > 0 ? Math.min(((limit - used) / limit) * 100, 100) : 0);

              return (
                <Grid item xs={12} md={6} key={product.productId}>
                  <motion.div variants={itemVariants}>
                    <ProductCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getProductTypeLabel(product.productType)} {product.institution ? `• ${product.institution}` : ''}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton onClick={() => handleOpenDialog(product)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(product.productId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          <Chip size="small" label={getStatusLabel(product.status)} />
                          <Chip size="small" icon={<PercentIcon />} label={`${product.interestRate || 0}%`} />
                          {product.productId === lowestRateId && (
                            <Chip size="small" color="success" label={t('agtfin.products.calculator.bestRate')} />
                          )}
                          {paymentAlert && (
                            <Chip
                              size="small"
                              color={paymentAlert.color}
                              icon={<WarningAmberIcon />}
                              label={paymentAlert.label}
                            />
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {isCredit ? (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Cupo usado
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatCurrency(used)} de {formatCurrency(limit)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={utilization}
                              sx={{
                                mt: 1,
                                height: 8,
                                borderRadius: 8,
                                backgroundColor: colors.backgrounds.light,
                                '& .MuiLinearProgress-bar': {
                                  background: colors.gradients.secondary
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Disponible: {formatCurrency(Math.max(limit - used, 0))}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Saldo actual
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatCurrency(used)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={paymentProgress}
                              sx={{
                                mt: 1,
                                height: 8,
                                borderRadius: 8,
                                backgroundColor: colors.backgrounds.light,
                                '& .MuiLinearProgress-bar': {
                                  background: colors.gradients.secondary
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {paymentsTotal > 0
                                ? `${paymentsMade}/${paymentsTotal} cuotas pagadas`
                                : t('agtfin.products.card.estimatedProgress')}
                            </Typography>
                          </>
                        )}

                        {(product.linkedBillId || product.linkedExpenseId) && (
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinkIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {product.linkedBillId ? `${t('agtfin.products.card.linkedBill')} ${billsLookup[product.linkedBillId] || t('agtfin.products.card.billLinked')}` : ''}
                              {product.linkedBillId && product.linkedExpenseId ? ' • ' : ''}
                              {product.linkedExpenseId ? `${t('agtfin.products.card.linkedExpense')} ${expensesLookup[product.linkedExpenseId] || t('agtfin.products.card.expenseLinked')}` : ''}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </ProductCard>
                  </motion.div>
                </Grid>
              );
            })
          )}
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SummaryCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Calculadora de intereses
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('agtfin.products.calculator.balance')}
                      size="small"
                      type="number"
                      value={calculator.balance}
                      onChange={(event) => setCalculator((prev) => ({
                        ...prev,
                        balance: event.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('agtfin.products.calculator.rate')}
                      size="small"
                      type="number"
                      value={calculator.rate}
                      onChange={(event) => setCalculator((prev) => ({
                        ...prev,
                        rate: event.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('agtfin.products.calculator.months')}
                      size="small"
                      type="number"
                      value={calculator.months}
                      onChange={(event) => setCalculator((prev) => ({
                        ...prev,
                        months: event.target.value
                      }))}
                    />
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Interés estimado: {formatCurrency(calculateInterest())}
                </Typography>
              </CardContent>
            </SummaryCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <SummaryCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingDownIcon fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Comparador de tasas
                  </Typography>
                </Box>
                {products.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Agrega productos para comparar tasas.
                  </Typography>
                ) : (
                  products
                    .slice()
                    .sort((a, b) => (a.interestRate || 0) - (b.interestRate || 0))
                    .slice(0, 4)
                    .map((product) => (
                      <Box key={product.productId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{product.name}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {product.interestRate || 0}%
                        </Typography>
                      </Box>
                    ))
                )}
              </CardContent>
            </SummaryCard>
          </Grid>
        </Grid>
      </ModernContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {editingProduct ? t('agtfin.products.form.edit') : t('agtfin.products.new')}
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('agtfin.products.form.productType')}
                select
                value={formData.productType}
                onChange={handleFormChange('productType')}
              >
                {getProductTypes(t).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('agtfin.products.form.name')}
                value={formData.name}
                onChange={handleFormChange('name')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('agtfin.products.form.institution')}
                value={formData.institution}
                onChange={handleFormChange('institution')}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('agtfin.products.calculator.rate')}
                type="number"
                value={formData.interestRate}
                onChange={handleFormChange('interestRate')}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label={t('agtfin.products.form.status')}
                value={formData.status}
                onChange={handleFormChange('status')}
              >
                {getStatusOptions(t).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {['credit_card', 'credit_line'].includes(formData.productType) ? (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cupo total"
                    type="number"
                    value={formData.creditLimit}
                    onChange={handleFormChange('creditLimit')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cupo usado"
                    type="number"
                    value={formData.balanceUsed}
                    onChange={handleFormChange('balanceUsed')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Disponible"
                    type="number"
                    value={formData.balanceAvailable}
                    onChange={handleFormChange('balanceAvailable')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de corte"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.billingDate}
                    onChange={handleFormChange('billingDate')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de pago"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.paymentDueDate}
                    onChange={handleFormChange('paymentDueDate')}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Monto original"
                    type="number"
                    value={formData.originalAmount}
                    onChange={handleFormChange('originalAmount')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Saldo actual"
                    type="number"
                    value={formData.currentBalance}
                    onChange={handleFormChange('currentBalance')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cuota mensual"
                    type="number"
                    value={formData.monthlyPayment}
                    onChange={handleFormChange('monthlyPayment')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Plazo (meses)"
                    type="number"
                    value={formData.termMonths}
                    onChange={handleFormChange('termMonths')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cuotas pagadas"
                    type="number"
                    value={formData.paymentsMade}
                    onChange={handleFormChange('paymentsMade')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cuotas totales"
                    type="number"
                    value={formData.paymentsTotal}
                    onChange={handleFormChange('paymentsTotal')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de pago"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.paymentDueDate}
                    onChange={handleFormChange('paymentDueDate')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Garantía/Propiedad"
                    value={formData.collateral}
                    onChange={handleFormChange('collateral')}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recordatorio (días)"
                type="number"
                value={formData.reminderDays}
                onChange={handleFormChange('reminderDays')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Moneda"
                value={formData.currency}
                onChange={handleFormChange('currency')}
                placeholder={currency}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Cuenta vinculada"
                value={formData.linkedBillId}
                onChange={handleFormChange('linkedBillId')}
              >
                <MenuItem value="">Sin vincular</MenuItem>
                {bills.map((bill) => (
                  <MenuItem key={bill.billId} value={bill.billId}>
                    {bill.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Gasto asociado"
                value={formData.linkedExpenseId}
                onChange={handleFormChange('linkedExpenseId')}
              >
                <MenuItem value="">Sin asociar</MenuItem>
                {expenses.map((expense) => (
                  <MenuItem key={expense.expenseId} value={expense.expenseId}>
                    {expense.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                multiline
                minRows={2}
                value={formData.notes}
                onChange={handleFormChange('notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingProduct ? t('agtfin.products.form.saveChanges') : t('agtfin.products.form.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default FinProducts;
